import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PlanSelectionStep from '@/pages/auth/components/PlanSelectionStep';
import { SLIDER_TICKS } from '@/pages/auth/components/planSelectionData';
import { OFFER, OFFER_END, compareGroups, pricingFaqs, type CompareRow } from '@/mocks/pricing';

// ---------------------------------------------------------------- banner

function useCountdown(end: string) {
  const target = new Date(end).getTime();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const left = Math.max(0, target - now);
  return {
    done: left === 0,
    parts: [
      { v: Math.floor(left / 86_400_000), l: 'Days' },
      { v: Math.floor(left / 3_600_000) % 24, l: 'Hours' },
      { v: Math.floor(left / 60_000) % 60, l: 'Mins' },
      { v: Math.floor(left / 1000) % 60, l: 'Secs' },
    ],
  };
}

function OfferBanner() {
  const { done, parts } = useCountdown(OFFER_END);
  if (done) return null;
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#f5333f] via-[#ff5b6b] to-[#ffd1dc] px-5 py-4 md:px-8 md:py-5 flex flex-col md:flex-row md:items-center gap-4">
      <div className="pointer-events-none absolute left-1/2 -top-10 w-56 h-56 rounded-full bg-white/15 blur-2xl" />
      <div className="relative flex-1 text-white">
        <div className="flex items-center gap-2 text-lg md:text-2xl font-extrabold tracking-tight">
          <i className="ri-fire-fill" /> {OFFER.title}
        </div>
        <div className="mt-0.5 text-xs md:text-sm text-white/90">{OFFER.subtitle}</div>
      </div>
      <div className="relative flex gap-2">
        {parts.map((p) => (
          <div key={p.l} className="w-14 md:w-16 rounded-xl bg-white/95 py-2 text-center shadow-sm">
            <div className="text-xl md:text-2xl font-extrabold text-[#f5333f] tabular-nums leading-none">{String(p.v).padStart(2, '0')}</div>
            <div className="mt-1 text-[10px] text-[#8f959e]">{p.l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- tables

function Cell({ value }: { value: CompareRow['values'][number] }) {
  if (value === true) return <i className="ri-checkbox-circle-fill text-lg text-[#8f959e]" aria-label="Included" />;
  if (value === false) return <span className="text-[#c9cdd4]" aria-label="Not included">—</span>;
  return <span className="text-xs text-[#1f2329]">{value}</span>;
}

function UsageTable() {
  const [more, setMore] = useState(false);
  const rows: { name: string; hint: string; cell: (i: number) => string }[] = [
    { name: 'Active members', hint: 'per month', cell: (i) => SLIDER_TICKS[i]!.members.toLocaleString() },
    { name: 'POS transactions', hint: 'per month', cell: (i) => SLIDER_TICKS[i]!.monthlyTxs.toLocaleString() },
    { name: 'Campaigns', hint: 'running at once', cell: (i) => SLIDER_TICKS[i]!.campaigns.toLocaleString() },
    { name: 'Growth Pro', hint: 'per month, 36-month term', cell: (i) => `$${SLIDER_TICKS[i]!.card2Monthly}` },
    { name: 'Enterprise VIP', hint: 'per month, 36-month term', cell: (i) => `$${SLIDER_TICKS[i]!.card3Monthly}` },
  ];
  const shown = more ? rows : rows.slice(0, 3);
  return (
    <div className="rounded-2xl bg-white overflow-x-auto">
      <table className="w-full min-w-[640px] text-left">
        <thead>
          <tr className="text-xs text-[#646a73]">
            <th className="px-5 py-4 font-medium">Member tier</th>
            {SLIDER_TICKS.map((tk) => (
              <th key={tk.label} className="px-4 py-4">
                <span className="text-base font-bold text-[#1f2329]">{tk.label}</span>
                <span className="ml-1 font-normal">members</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((r) => (
            <tr key={r.name} className="border-t border-[#f0f1f3]">
              <td className="px-5 py-3.5">
                <div className="text-sm font-semibold text-[#1f2329]">{r.name}</div>
                <div className="text-[11px] text-[#a3a8b0]">{r.hint}</div>
              </td>
              {SLIDER_TICKS.map((tk, i) => (
                <td key={tk.label} className="px-4 py-3.5 text-sm text-[#1f2329]">
                  {r.cell(i)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        onClick={() => setMore(!more)}
        className="w-full text-left px-5 py-3 text-xs text-[#646a73] hover:text-[#1f2329] border-t border-[#f0f1f3] cursor-pointer"
      >
        {more ? 'Show less' : 'Show prices per tier'} <i className={more ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
      </button>
    </div>
  );
}

function CompareTable() {
  return (
    <div className="rounded-2xl bg-white overflow-x-auto">
      <table className="w-full min-w-[640px] text-left">
        <thead>
          <tr className="text-sm text-[#1f2329] border-b border-[#f0f1f3]">
            <th className="px-5 py-4 font-semibold w-[46%]">Features</th>
            <th className="px-4 py-4 font-semibold text-center">Starter</th>
            <th className="px-4 py-4 font-semibold text-center text-[#2f51d6]">Growth Pro</th>
            <th className="px-4 py-4 font-semibold text-center">Enterprise VIP</th>
          </tr>
        </thead>
        {compareGroups.map((g) => (
          <tbody key={g.title}>
            <tr>
              <td colSpan={4} className="px-5 pt-6 pb-2">
                <span className="border-l-[3px] border-[#1f2329] pl-2 text-sm font-bold text-[#1f2329]">{g.title}</span>
              </td>
            </tr>
            {g.rows.map((r) => (
              <tr key={r.name} className="border-t border-[#f5f6f7]">
                <td className="px-5 py-3">
                  <div className="text-[13px] font-semibold text-[#1f2329]">{r.name}</div>
                  {r.hint && <div className="text-[11px] text-[#a3a8b0]">{r.hint}</div>}
                </td>
                {r.values.map((v, i) => (
                  <td key={i} className="px-4 py-3 text-center">
                    <Cell value={v} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- FAQ

function Faq() {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="space-y-2">
      {pricingFaqs.map((f, i) => {
        const isOpen = open === i;
        return (
          <div key={f.q} className="rounded-xl bg-white">
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : i)}
              aria-expanded={isOpen}
              className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
            >
              <span className="text-sm font-semibold text-[#1f2329]">{f.q}</span>
              <i className={`${isOpen ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#646a73]`} />
            </button>
            {isOpen && <p className="px-5 pb-4 -mt-1 text-[13px] leading-relaxed text-[#646a73]">{f.a}</p>}
          </div>
        );
      })}
      <p className="pt-4 text-center text-xs text-[#8f959e]">
        Still have questions?{' '}
        <a href={`${import.meta.env.BASE_URL}#contact`} className="text-[#2f51d6] hover:underline">
          Talk to our team
        </a>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------- page

export default function PricingPage() {
  const navigate = useNavigate();
  useEffect(() => {
    document.title = 'Pricing | LoyaltyBridge';
  }, []);

  return (
    <div className="bg-[#f2f3f5] pt-24 pb-20">
      <div className="max-w-6xl mx-auto px-4 md:px-6 space-y-6">
        <OfferBanner />

        <PlanSelectionStep
          lang="en"
          hideHeaderTitle
          onSelectPlan={(planId) => navigate(`/login?plan=${encodeURIComponent(planId)}`)}
        />

        <div className="flex justify-center">
          <a href="#compare" className="inline-flex items-center gap-1 text-sm text-[#646a73] hover:text-[#1f2329]">
            Compare all plan features <i className="ri-arrow-down-s-line" />
          </a>
        </div>

        <section className="pt-10">
          <h2 className="text-center text-2xl md:text-[28px] font-bold text-[#1f2329] tracking-tight mb-6">Usage included by member tier</h2>
          <UsageTable />
        </section>

        <section id="compare" className="pt-14 scroll-mt-20">
          <h2 className="text-center text-2xl md:text-[28px] font-bold text-[#1f2329] tracking-tight mb-6">Compare plans</h2>
          <CompareTable />
        </section>

        <section className="pt-14 max-w-3xl mx-auto">
          <h2 className="text-center text-2xl md:text-[28px] font-bold text-[#1f2329] tracking-tight mb-6">Frequently asked questions</h2>
          <Faq />
        </section>

        <div className="pt-6 text-center">
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 h-11 px-7 rounded-full bg-[#1f2329] text-white text-sm font-semibold hover:bg-black transition-colors"
          >
            Start free <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </div>
  );
}
